<template>
  <v-container fluid>
    <v-row class="child-flex">
      <v-card>
        <v-card-text>
          <v-card-title>
            New CSV ROI Report
          </v-card-title>
          <v-form ref="form">
            <v-row no-gutters>
              <v-col cols="1" offset="1">
                <v-subheader>Csv Datasource</v-subheader>
              </v-col>
              <v-col cols="6">
                <span v-if="sourceDatasourceColumnIssue" class="red--text">{{ sourceDatasourceColumnIssue }}</span>
                <DatasourceSelect v-model="sourceCsvDatasourceId" :dealer_id="dealer.id" datasource-type="CsvDatasource"
                  v-on:selected-datasource="sourceCsvDatasource = $event">
                </DatasourceSelect>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="1" offset="1">
                <v-subheader>Start Date</v-subheader>
              </v-col>
              <v-col cols="3">
                <YMDDatePicker v-model="roiStartDate" label="Start Date" required :rules="vruleFieldRequired"
                  hint="*Required" persistent-hint></YMDDatePicker>
              </v-col>
              <v-col cols="3" offset="1">
                <template v-if="numberOfDays">
                  <h1 class="offset-header green--text">{{ numberOfDays }}</h1>
                  <v-subheader>Number of days in report</v-subheader>
                </template>
              </v-col>

            </v-row>
            <v-row no-gutters>
              <v-col cols="1" offset="1">
                <v-subheader>End Date</v-subheader>
              </v-col>
              <v-col cols="3">
                <YMDDatePicker v-model="roiEndDate" label="End Date" required :rules="vruleFieldRequired"
                  hint="*Required" persistent-hint></YMDDatePicker>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="1" offset="1">
                <v-subheader>Days Between</v-subheader>
              </v-col>
              <v-col cols="3">
                <v-text-field v-model="roiDaysBetween" placeholder="Days Between" persistent-hint
                  hint="Minimum Days Between Transactions" suffix="days" required :rules="vruleFieldRequired"
                  type="number" min="0" step="1"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="1" offset="1">
                <v-subheader>Report Type</v-subheader>
              </v-col>
              <v-col cols="3">
                <v-radio-group v-model="roiType" required :rules="vruleFieldRequired">
                  <v-radio label="Attribute any Customer transactions" value="customer"></v-radio>
                  <v-radio label="Attribute Customer and VIN specific transactions" value="customer+vin"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="roiType === 'customer+vin'">
              <v-col cols="1" offset="1">
                <v-subheader>Map VIN</v-subheader>
              </v-col>
              <v-col cols="3">
                <v-select v-model="vinColumn" :items="sourceCsvDatasourceColumns" label="Select VIN Column" required
                  :rules="vruleFieldRequired"></v-select>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" class="text-center">
                <v-btn color="primary" @click="validateAndSubmit" :loading="loading" :disabled="loading">
                  Generate Report
                </v-btn>
                <v-btn class="ml-4" @click="$router.back()" :disabled="loading">
                  Cancel
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import useCurrentDealer from '@/composables/useCurrentDealer'
import useValidation from '@/composables/useValidation'
import { DatasourceSelect, YMDDatePicker } from '@/components/inputs'
import { DateTime } from 'luxon'
import { CsvRoiReportsApi } from '@/services'
import useLogger from "@/composables/useLogger";
import useFlashMessage from '@/composables/useFlashMessage'
import { pathOr } from 'ramda'
import { useRouter } from 'vue-router/composables'

const logger = useLogger("csv-roi-report-new");
const flash = useFlashMessage()
const router = useRouter()

const { vruleFieldRequired } = useValidation()

const dealer = useCurrentDealer()
const sourceCsvDatasourceId = ref(null)
const sourceCsvDatasource = ref({})
const vinColumn = ref(null)
const reportDate = ref()
const roiStartDate = ref()
const roiEndDate = ref()
const roiDaysBetween = ref(null)
const roiType = ref('customer+vin')
const loading = ref(false)

const form = ref(null) // initialize to null - will be populated by el ref=form

const sourceCsvDatasourceColumns = computed(() => {
  return pathOr([], ['meta', 'headers'], sourceCsvDatasource.value)
})
const sourceDatasourceColumnIssue = computed(() => {
  // check to see if customer_id is in the sourceCsvDatasourceColumns
  if (sourceCsvDatasource.value.id && !sourceCsvDatasourceColumns.value.includes('customer_id')) {
    return 'Source Csv Datasource must contain a customer_id column'
  }
  return null
})
watch(sourceCsvDatasourceColumns, (newVal, oldVal) => {
  // reset vinColumn if csvDatasourceColumns change
  logger.info('sourceCsvDatasourceColumns', newVal)
  if (newVal !== oldVal) {
    vinColumn.value = null
  }
})

const numberOfDays = computed(() => {
  if (roiStartDate.value && roiEndDate.value) {
    return DateTime.fromISO(roiEndDate.value).diff(DateTime.fromISO(roiStartDate.value), 'days').toObject().days
  } else {
    return null
  }
})

function setDefaultTimeframe() {
  reportDate.value = DateTime.now().toISODate()
  roiStartDate.value = DateTime.now().minus({ days: 30 }).toISODate()
  roiEndDate.value = DateTime.now().toISODate()
  roiDaysBetween.value = 7
}

async function validateAndSubmit() {
  if (form.value.validate()) {
    if (!sourceDatasourceColumnIssue.value) {
      await submit()
    }
  }
}

async function submit() {
  loading.value = true
  try {
    const resp = await CsvRoiReportsApi.createCsvRoiReport({
      dealer_id: dealer.value.id,
      source_csv_datasource_id: sourceCsvDatasourceId.value,
      roi_start_date: roiStartDate.value,
      roi_end_date: roiEndDate.value,
      roi_days_between: roiDaysBetween.value,
      roi_type: roiType.value,
      vin_column_mapping: vinColumn.value
    })
    logger.info('csvRoiReport', resp)
    router.push({ name: 'csv_datasource_reports_path' })
    flash({
      showClose: true,
      message: `Roi Report Created - ${resp.data.name}`,
      type: 'success',
      duration: 2000
    })
  } catch (error) {
    logger.error('Error creating CSV ROI Report', error.message)
    flash({
      showClose: true,
      message: 'Error Creating Report. Reload to try again',
      type: 'error',
      duration: 0
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  setDefaultTimeframe()
})
</script>
